import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import requestUser from "../../services/requestUser";
import { baseApiUrl } from "../../services/api";

// FormNotify component for displaying alerts
const FormNotify = ({ bg, title, message }) => {
  return (
    <div className={`alert alert-${bg}`} role="alert">
      {title && <strong>{title}</strong>}
      {message}
    </div>
  );
};

const Register = () => {
  const navigate = useNavigate();
  const [registerFail, setRegisterFail] = useState(false);
  const [errorMessage, setErrorMessage] = useState("Votre mail est déjà utilisé.");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [roles, setRoles] = useState([]);
  const [roleId, setRoleId] = useState(5); // Par défaut le rôle lecteur (5)
  const [photo, setPhoto] = useState(null);

  // État pour les données utilisateur
  const [nom, setNom] = useState("");
  const [prenom, setPrenom] = useState("");
  const [email, setEmail] = useState("");
  const [mdp, setMdp] = useState("");
  const [siteWeb, setSiteWeb] = useState("");
  const [dateDeNaissance, setDateDeNaissance] = useState("");
  const [adresse, setAdresse] = useState({
    adresse1: "",
    adresse2: "",
    ville: "",
    codePostale: "",
    pays: ""
  });

  // Récupération de la liste des rôles au chargement du composant
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await baseApiUrl.get("/role/liste");
        console.log("Rôles récupérés:", response.data);
        setRoles(response.data);
      } catch (error) {
        console.error("Erreur lors du chargement des rôles", error);
      }
    };

    fetchRoles();
  }, []);

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  const handlePhotoChange = (e) => {
    setPhoto(e.target.files[0]);
  };

  const handleAdresseChange = (e) => {
    const { name, value } = e.target;
    setAdresse((prevAdresse) => ({
      ...prevAdresse,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setRegisterFail(false);

    // Validation des champs
    if (!nom || !email || !mdp) {
      setErrorMessage("Veuillez remplir tous les champs obligatoires.");
      setRegisterFail(true);
      return;
    }

    try {
      console.log("Début de l'inscription");

      const formData = new FormData();
      const utilisateurData = {
        nom,
        prenom,
        siteWeb,
        mdp,
        email,
        roleId,
        dateDeNaissance,
        adresse,
        actif: true // Le compte est activé d'office
      };

      console.log("Données utilisateur:", utilisateurData);
      formData.append(
        "utilisateur",
        new Blob([JSON.stringify(utilisateurData)], {
          type: "application/json",
        })
      );

      if (photo) {
        console.log("Photo ajoutée:", photo);
        formData.append("image", photo);
      }

      // Affichage du contenu du FormData pour débogage
      for (let pair of formData.entries()) {
        console.log(pair[0] + ": " + pair[1]);
      }

      // Appel API pour l'inscription (utilisant la même structure que la création d'utilisateur admin)
      const response = await baseApiUrl.post("/utilisateur/create", formData);

      console.log("Réponse de l'API:", response);

      if (response.status === 200 || response.status === 201) {
        // Redirection vers la page de connexion après inscription réussie
        navigate("/login", { state: { message: "Inscription réussie. Vous pouvez maintenant vous connecter." } });
      } else {
        const errorText = await response.text();
        console.error("Erreur API:", errorText);
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
    } catch (error) {
      setRegisterFail(true);
      if (error.response && error.response.data && error.response.data.message) {
        setErrorMessage(error.response.data.message);
      } else {
        setErrorMessage("Une erreur s'est produite lors de l'inscription.");
      }
      console.error("Erreur d'inscription:", error);
    }
  };

  return (
    <div className="container-fluid d-flex justify-content-center align-items-center"
         style={{ minHeight: "100vh", backgroundColor: "#f0f0f0" }}>
      <div className="row w-100"
           style={{ maxWidth: "900px", backgroundColor: "#fff", borderRadius: "15px", boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)" }}>

        {/* Left Side: Form */}
        <div className="col-lg-6 col-md-6 col-sm-12 d-flex justify-content-center align-items-center" style={{ padding: "30px" }}>
          <div className="w-100">
            <img src={require('../.././assets/imgs/logo_pnud.png')} alt="Logo"
                 style={{ maxWidth: "120px", display: "block", margin: "0 auto" }} />
            <p className="text-center">Créez votre compte</p>

            <form onSubmit={handleSubmit} className="w-100">
              {registerFail && (
                <div className="mb-3">
                  <FormNotify
                    bg={"danger"}
                    title={""}
                    message={errorMessage}
                  />
                </div>
              )}

              <div className="form-floating mb-3">
                <input
                  type="text"
                  className="form-control"
                  id="nomInput"
                  placeholder="Nom"
                  value={nom}
                  onChange={(e) => setNom(e.target.value)}
                  required
                />
                <label htmlFor="nomInput">Nom</label>
              </div>

              <div className="form-floating mb-3">
                <input
                  type="text"
                  className="form-control"
                  id="prenomInput"
                  placeholder="Prénom"
                  value={prenom}
                  onChange={(e) => setPrenom(e.target.value)}
                />
                <label htmlFor="prenomInput">Prénom</label>
              </div>

              <div className="form-floating mb-3">
                <input
                  type="email"
                  className="form-control"
                  id="emailInput"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <label htmlFor="emailInput">Email</label>
              </div>

              <div className="form-floating mb-3 position-relative">
                <input
                  type={passwordVisible ? "text" : "password"}
                  className="form-control"
                  id="passwordInput"
                  placeholder="Mot de passe"
                  value={mdp}
                  onChange={(e) => setMdp(e.target.value)}
                  required
                />
                <i
                  className={`fas ${passwordVisible ? "fa-eye-slash" : "fa-eye"} position-absolute end-0 top-50 translate-middle-y px-2`}
                  onClick={togglePasswordVisibility}
                  style={{ cursor: "pointer" }}
                ></i>
                <label htmlFor="passwordInput">Mot de passe</label>
              </div>

              <div className="mb-3">
                <label htmlFor="photoInput" className="form-label">Photo de profil</label>
                <input
                  type="file"
                  className="form-control"
                  id="photoInput"
                  onChange={handlePhotoChange}
                  accept="image/*"
                />
              </div>

              <div className="form-group mb-0">
                <button className="btn btn-primary w-100" type="submit" style={{ background: "#007bff" }}>
                  S'enregistrer
                </button>
              </div>
            </form>

            <div className="text-center mt-3">
              <span>Avez-vous déjà un compte?</span>
              <Link to="/login" className="btn btn-outline-primary ms-2">Se connecter</Link>
            </div>
          </div>
        </div>

        {/* Right Side: Branding & Message */}
        <div className="col-lg-6 col-md-6 col-sm-12 d-flex justify-content-center align-items-center"
             style={{ background: "linear-gradient(to right,rgb(83, 216, 136),rgb(28, 147, 30))", color: "#fff", padding: "30px",
                      borderTopRightRadius: "15px", borderBottomRightRadius: "15px" }}>
          <div className="text-center">
            <p>
              Le compte que vous allez créer vous donne droit à juste liker et commenter. Si vous voulez plus d'accès, veuillez nous contacter.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
